import React from 'react';
import moment from 'moment';
import { TimelineTypes } from '../../timeLineTypes';
import { translateCountMapToString } from '../translateCountMapToString';

export const getBgCharacteristics = (
  item: any,
  itemWidth: number,
  clinician_id: any,
): React.CSSProperties => {
  const style: React.CSSProperties = {
    background: 'transparent',
    borderWidth: '0px',
    borderStyle: 'solid',
    borderColor: 'transparent',
    width: '0px',
    borderRadius: '20px',
    zIndex:
      item.type === 'dotBlue' || 'dotDarkBlue' || 'dotPurple' ? 1000 : 999,
  };

  if (
    item.type == 'redBell' ||
    item.type == 'yellowBell' ||
    item.type == 'greenBell' ||
    item.type == 'greenZ' ||
    item.type === 'yellowZ' ||
    item.type === 'greenArrowUp' ||
    item.type === 'redZ' ||
    item.type === 'notesCircle' ||
    item.type === 'bell' ||
    item.type === 'purplePencil' ||
    item.type === 'redArrowUp' ||
    item.type === 'redArrowDown' ||
    item.type === 'yellowArrowDown' ||
    item.type == 'shield'
  ) {
    style.marginTop = '8px';
  }

  switch (item.type) {
    case 'redBell':
      style.borderWidth = '8px';
      style.borderStyle = 'solid';
      style.borderColor = 'rgba(255,0,0,0.09)';
      style.display = 'flex';
      style.alignItems = 'center';
      style.justifyContent = 'center';
      break;
    case 'greenBell':
      style.borderWidth = '8px';
      style.borderStyle = 'solid';
      style.borderColor = 'rgba(0,255,0,0.32)';
      style.display = 'flex';
      style.alignItems = 'center';
      style.justifyContent = 'center';
      break;
    case 'yellowBell':
      style.borderWidth = '8px';
      style.borderStyle = 'solid';
      style.borderColor = 'rgba(227,186,10,0.32)';
      style.display = 'flex';
      style.alignItems = 'center';
      style.justifyContent = 'center';
      break;
    case 'action':
      style.maxHeight = '20px';
      style.alignItems = 'center';
      style.marginTop = '17px';
      style.justifyContent = 'bottom';
      style.background =
        clinician_id === item.clinician_id ? '#DB54B3' : '#654B78';
      break;
    case 'shield':
      style.background = 'rgba(206,206,206,0.32)';
      style.borderWidth = '0px';
      style.borderStyle = 'none';
      style.display = 'flex';
      style.alignItems = 'center';
      style.justifyContent = 'center';
      break;
    default:
      break;
  }

  if (
    item.action_status === 'E' ||
    item.action_status === 'A' ||
    item.action_status === 'D' ||
    item.action_status === 'C' ||
    item.action_status === 'S' ||
    item.action_status === 'I'
  ) {
    style.borderWidth = '0px';
    style.borderStyle = 'none';
  }

  if (
    item.type === 'redBell' ||
    item.type === 'yellowBell' ||
    item.type === 'greenBell' ||
    item.type === 'shield'
  ) {
    style.maxHeight = '30px';
    style.marginTop = '14px';

    if (
      item.action_status === 'E' ||
      item.action_status === 'A' ||
      item.action_status === 'D' ||
      item.action_status === 'C' ||
      item.action_status === 'S' ||
      item.action_status === 'I'
    ) {
      style.background = 'rgba(206,206,206,0.32)';
    } else {
      style.background = 'rgba(235,235,235,0.32)';
    }
    style.background =
      itemWidth < 30 && item.action_status !== 'I'
        ? 'transparent'
        : style.background;
  }

  if (item.type === 'shield') {
    style.maxHeight = '25px';
    style.marginTop = '15px';
  }

  if (item.action_status === 'I') {
    // Ignored insights are now to be hidden as per AI2V2-380
    style.display = 'none';
  }

  return style;
};

export const compareSigns = (item: any): React.ReactNode => {
  let listItems: React.ReactNode[] = [];

  const hasWarningSigns =
    Array.isArray(item.warning_signs) && item.warning_signs.length > 0;
  const hasGoodSigns =
    Array.isArray(item.good_signs) && item.good_signs.length > 0;

  const listItemStyle: React.CSSProperties = {
    listStyleType: 'none',
    lineHeight: '1.2',
    marginTop: '0',
    marginBottom: '0.5em',
  };

  if (
    hasWarningSigns &&
    (!hasGoodSigns || item.warning_signs.length > item.good_signs.length)
  ) {
    listItems = listItems.concat(
      item.warning_signs.map((sign: string, idx: number) => (
        <li key={`warning-${idx}`} style={listItemStyle}>
          <u>{sign}</u>
        </li>
      )),
    );
  }

  if (
    hasGoodSigns &&
    (!hasWarningSigns || item.good_signs.length >= item.warning_signs.length)
  ) {
    listItems = listItems.concat(
      item.good_signs.map((sign: string, idx: number) => (
        <li key={`good-${idx}`} style={listItemStyle}>
          <u>{sign}</u>
        </li>
      )),
    );
  }

  if (listItems.length > 0) {
    return (
      <>
        <li style={{ ...listItemStyle, marginBottom: '1em' }}>
          <strong>Signature Signs</strong>
        </li>
        {listItems}
      </>
    );
  }
  return null;
};

export const getShortSummary = (
  item: any,
  countMap?: Map<string, number>,
): React.ReactNode => {
  if (item.settings?.dosageChange) {
    return `Changed to ${item.settings?.dosageQty} ${item.settings?.dosageFrequency}`;
  } else if (item.pd_dispense_record_link) {
    return 'Medication picked up';
  } else if (item.pd_prescription_record_link) {
    return 'Medication prescribed';
  } else if (String(item.group_type).startsWith('Appointment')) {
    return 'Appointment attendance';
  } else if (item.group_type == 'Diagnostic and Pathology') {
    return 'Diagnostic/Pathology event';
  } else if (item.group_type == 'Interventions') {
    return 'Interventions event';
  } else if (item.group_type == 'Cardio tests/imaging') {
    return 'Cardio tests/imaging event';
  } else if (item.group_type == 'Lipids and HDL') {
    return 'Lipids and HDL event';
  } else if (item.group_type == 'Other services') {
    return 'Other service event';
  } else if (String(item.group_type).startsWith('MBS')) {
    return 'Uncategorised MBS event';
  } else if (String(item.group_type).startsWith('Meds')) {
    return 'Medication picked up';
  } else if (item.group_type === 'Profile Checks') {
    return 'Profile was viewed';
  } else if (String(item.group_type).startsWith('Profile')) {
    return item.interaction_type;
  } else if (item.group_type === 'Insights') {
    return item.why_created;
  } else if (item.type === 'action') {
    return item.description;
  } else if (item.detail) {
    return item.detail.description || item.event_name;
  } else if (item.warning_signs || item.good_signs) {
    const signsList = compareSigns(item);
    return signsList ? (
      <ul style={{ listStyleType: 'none', paddingLeft: '0' }}>{signsList}</ul>
    ) : (
      'No signs information'
    );
  } else if (
    (item.group_type === TimelineTypes.WEEK_EVENTS ||
      item.group_type === TimelineTypes.MONTH_EVENTS) &&
    countMap
  ) {
    return translateCountMapToString(countMap);
  } else {
    return 'No detailed information';
  }
};

export const getIconTypeForWellbeingFeedback = (
  icons: any,
  detailValue: number,
  eventName: string,
): string | null | undefined => {
  const wellbeingFeedbackIcons = icons[eventName];
  if (!wellbeingFeedbackIcons) {
    return;
  }

  for (const icon in wellbeingFeedbackIcons) {
    const criteria = wellbeingFeedbackIcons[icon];
    const maxCriteria = Object.prototype.hasOwnProperty.call(criteria, 'max')
      ? criteria['max']
      : Infinity;
    const minCriteria = Object.prototype.hasOwnProperty.call(criteria, 'min')
      ? criteria['min']
      : 0;

    if (detailValue <= maxCriteria && detailValue >= minCriteria) {
      return icon;
    }
  }
  return null;
};

// ============== Additional Pure Helpers (Grouping/Collapsing) ==============

export const getParentGroupIdFromGroups = (
  groups: any[],
  childId: any,
): any => {
  const out = groups.find((x) => x.id == childId)?.parent;
  return out;
};

export const getRelatedCollapsedGroups = (
  groups: any[],
  collapsedParentGroups: Record<string, boolean>,
): any[] => {
  const relatedCollapsedGroups: any[] = [];
  groups
    .filter((g) => collapsedParentGroups[g.parent])
    .map((dg) => {
      return {
        children: [dg, ...groups.filter((f) => f.parent === dg.id)],
      };
    })
    .forEach((d) => {
      relatedCollapsedGroups.push(...d.children);
    });
  return relatedCollapsedGroups;
};

export const updateItemsForCollapsedGroups = (
  items: any[],
  collapsedParentGroups: Record<string, boolean>,
  groups: any[],
  originalItems: any[],
  relatedCollapsedGroupsParam?: any[],
): any[] => {
  const relatedCollapsedGroups =
    relatedCollapsedGroupsParam ??
    getRelatedCollapsedGroups(groups, collapsedParentGroups);

  let newItems = [...items];

  Object.entries(collapsedParentGroups).forEach(([groupId, isCollapsed]) => {
    const rootRelatives = relatedCollapsedGroups.filter(
      (x) => x.rootId == groupId,
    );

    if (isCollapsed) {
      // Handle root relatives collapsing
      newItems = newItems.map((item) => {
        const relative = rootRelatives.find((r) => item.group === r.id);
        if (relative) {
          return {
            ...item,
            original_group: item.group,
            group: Number.parseInt(groupId),
          };
        }
        return item;
      });

      // Handle items without root relatives
      if (rootRelatives.length === 0) {
        newItems = newItems.map((item) =>
          getParentGroupIdFromGroups(groups, item.group) ===
          Number.parseInt(groupId)
            ? {
                ...item,
                original_group: item.group,
                group: getParentGroupIdFromGroups(groups, item.group),
              }
            : item,
        );
      }
    } else {
      // Restore original grouping
      newItems = newItems.map((item) =>
        item.group === Number.parseInt(groupId)
          ? {
              ...item,
              group:
                originalItems.find((x) => x.id === item.id)?.group ??
                item.group,
            }
          : item,
      );
    }
  });
  return newItems;
};

const getRepresentativeEvent = (events: any[]): any => {
  if (!Array.isArray(events) || events.length === 0) return null;

  const getEventStart = (ev: any): number => {
    if (typeof ev?.start_time === 'number') return ev.start_time;
    if (typeof ev?.event_date === 'string')
      return moment(ev.event_date).valueOf();
    if (typeof ev?.when_created === 'string')
      return moment(ev.when_created).valueOf();
    if (typeof ev?.created_at === 'string')
      return moment(ev.created_at).valueOf();
    return 0;
  };

  const isScriptWritten = (ev: any): boolean => {
    if (String(ev?.type) === 'scriptWritten') return true;
    return String(ev?.detail?.scriptWritten).toLowerCase() === 'true';
  };

  const scriptWrittenEvents = events.filter(isScriptWritten);
  const pool = scriptWrittenEvents.length > 0 ? scriptWrittenEvents : events;

  return pool.reduce((best, cur) => {
    if (!best) return cur;
    return getEventStart(cur) >= getEventStart(best) ? cur : best;
  }, pool[0]);
};

const isScriptWrittenEvent = (ev: any): boolean => {
  if (String(ev?.type) === 'scriptWritten') return true;
  return String(ev?.detail?.scriptWritten).toLowerCase() === 'true';
};

const isEmrOrderLike = (ev: any): boolean => {
  if (!ev) return false;
  if (String(ev?.type) === 'emrOrder') return true;
  if (ev?.detail?.emrOrder === true) return true;
  return String(ev?.detail?.fhir?.resourceType) === 'MedicationRequest';
};

const isHomeMedicationLike = (ev: any): boolean => {
  if (!ev) return false;
  if (String(ev?.type) === 'homeMedication') return true;
  if (ev?.detail?.homeMedication === true) return true;
  return String(ev?.detail?.fhir?.resourceType) === 'MedicationDispense';
};

const getCombinedTypeForMonthViews = (
  groupEvents: any[],
  representativeType: string,
): string => {
  if (groupEvents.some(isScriptWrittenEvent)) return representativeType;
  if (
    groupEvents.some(isEmrOrderLike) &&
    groupEvents.some(isHomeMedicationLike)
  ) {
    return 'emrOrderAndHome';
  }
  return representativeType;
};

const getCombinedTypeForWeekViews = (
  groupEvents: any[],
  representativeType: string,
): string => {
  if (groupEvents.some(isScriptWrittenEvent)) return representativeType;
  if (
    groupEvents.some(isEmrOrderLike) &&
    groupEvents.some(isHomeMedicationLike)
  ) {
    return 'emrOrderAndHome';
  }
  return representativeType;
};

export const combineMonthEvents = (
  groupedByMonth: Record<string, Record<string, any[]>>,
): any[] => {
  return Object.entries(groupedByMonth).reduce((acc, [key, value]) => {
    const monthStart = moment(key);
    const start_time = monthStart.add(2, 'day').valueOf();
    const end_time = monthStart.add(3, 'day').valueOf();

    const combinedEvents = Object.entries(value).map(
      ([groupId, groupEvents]) => {
        const groupIdNumber = Number.parseInt(groupId);
        const representativeEvent =
          getRepresentativeEvent(groupEvents) ?? groupEvents[0];
        return {
          start_time,
          end_time,
          group: groupIdNumber,
          group_type: TimelineTypes.MONTH_EVENTS,
          id: 'groupViewMonth-' + key + `-${groupId}`,
          parent_name:
            representativeEvent?.parent_name ?? groupEvents[0]?.parent_name,
          showInCollapsed: true,
          showInComplex: true,
          showInExpanded: true,
          showInSimple: true,
          type: getCombinedTypeForMonthViews(
            groupEvents,
            representativeEvent?.type ?? groupEvents[0]?.type,
          ),
          checks: [],
          cnt: 0,
          children: groupEvents,
          item_group_type:
            representativeEvent?.group_type ?? groupEvents[0]?.group_type,
        };
      },
    );

    return [...acc, ...combinedEvents];
  }, [] as any[]);
};

export const combineWeekEvents = (
  groupedByWeek: Record<string, Record<string, any[]>>,
): any[] => {
  return Object.entries(groupedByWeek).reduce((acc, [key, value]) => {
    const weekStart = moment(key);
    const start_time = weekStart.valueOf();
    const end_time = weekStart.add(1, 'week').valueOf();

    const combinedEvents = Object.entries(value).map(
      ([groupId, groupEvents]) => {
        const groupIdNumber = Number.parseInt(groupId);
        const representativeEvent =
          getRepresentativeEvent(groupEvents) ?? groupEvents[0];

        return {
          start_time,
          end_time,
          group: groupIdNumber,
          group_type: TimelineTypes.WEEK_EVENTS,
          id: 'groupViewWeek-' + key + `-${groupId}`,
          parent_name:
            representativeEvent?.parent_name ?? groupEvents[0]?.parent_name,
          showInCollapsed: true,
          showInComplex: true,
          showInExpanded: true,
          showInSimple: true,
          type: getCombinedTypeForWeekViews(
            groupEvents,
            representativeEvent?.type ?? groupEvents[0]?.type,
          ),
          checks: [],
          cnt: 0,
          children: groupEvents,
          item_group_type:
            representativeEvent?.group_type ?? groupEvents[0]?.group_type,
        };
      },
    );

    return [...acc, ...combinedEvents];
  }, [] as any[]);
};

export const computeOpenGroupIds = (
  openGroups: Record<string, boolean>,
): number[] => {
  return Object.keys(openGroups).reduce((acc: number[], key: string) => {
    if (openGroups[key]) {
      acc.push(Number.parseInt(key));
    }
    return acc;
  }, []);
};

export const shouldShowPathologyTable = (
  newGroups: any[],
  openGroupIds: number[],
  observationGroupType: string,
): boolean => {
  return Array.isArray(newGroups)
    ? newGroups.some((group) => {
        return (
          group.ogTitle === observationGroupType &&
          Array.isArray(openGroupIds) &&
          openGroupIds.find((openId) => openId === group.id)
        );
      })
    : false;
};
